import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Flame, PlayCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useLang, pickLocalized, tx } from '../../i18n/LanguageContext';
import { formatViews, isVideoUrl, posterFor } from './articleUtils';

const NewsletterBox = () => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/newsletter', { email, lang });
      toast.success(data.already ? L('بريدك مشترك مسبقاً', 'You are already subscribed') : L('تم الاشتراك بنجاح، ستصلك أحدث المقالات', 'Subscribed! New articles will reach you'));
      setEmail('');
    } catch {
      toast.error(L('يرجى إدخال بريد إلكتروني صحيح', 'Please enter a valid email'));
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="articles-newsletter" className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 to-sky-100/70 border border-blue-100 p-6">
      <Send className="absolute top-5 end-5 w-12 h-12 text-blue-200 rtl:-scale-x-100" />
      <h3 className="relative text-lg sm:text-xl font-extrabold text-slate-900 mb-1">{L('احصل على أحدث المقالات', 'Get the latest articles')}</h3>
      <p className="relative text-slate-600 text-sm mb-5">{L('اشترك لتصلك المقالات الجديدة مباشرة', 'Subscribe to receive new articles directly')}</p>
      <form onSubmit={submit} className="relative space-y-3">
        <input data-testid="articles-newsletter-input" type="email" required value={email} onChange={e => setEmail(e.target.value)}
          placeholder={L('أدخل بريدك الإلكتروني', 'Enter your email')}
          className="w-full h-12 rounded-xl bg-white border border-slate-200 px-4 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-[border-color,box-shadow]" />
        <button data-testid="articles-newsletter-submit" type="submit" disabled={busy}
          className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold inline-flex items-center justify-center gap-2 transition-colors">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}{L('اشترك الآن', 'Subscribe now')}
        </button>
      </form>
    </div>
  );
};

const RANK_COLORS = ['bg-blue-600 text-white', 'bg-slate-200 text-slate-700', 'bg-orange-500 text-white'];

const MostRead = ({ posts }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  if (!posts.length) return null;
  return (
    <div data-testid="articles-most-read" className="rounded-2xl bg-white border border-slate-200 p-6">
      <h3 className="flex items-center gap-2 text-lg font-extrabold text-slate-900 mb-5"><Flame className="w-5 h-5 text-orange-500" />{L('الأكثر قراءة', 'Most read')}</h3>
      <ol className="space-y-4">
        {posts.map((p, i) => (
          <li key={p.id}>
            <Link to={`/library/post/${p.id}`} data-testid={`most-read-${p.id}`} className="group flex items-center gap-3">
              <span className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${RANK_COLORS[i] || 'bg-slate-100 text-slate-500'}`}>{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-slate-900 text-sm leading-snug line-clamp-2 group-hover:text-blue-600 transition-colors">{pickLocalized(p.title, lang)}</div>
                <div className="text-xs text-slate-400 mt-1" data-testid={`most-read-views-${p.id}`}>{formatViews(p.views)} {L('مشاهدة', 'views')}</div>
              </div>
              <div className="w-20 h-14 shrink-0 rounded-lg overflow-hidden bg-slate-100">
                {isVideoUrl(p.cover) && !posterFor(p.cover) ? <video src={p.cover} muted preload="metadata" className="w-full h-full object-cover" /> : <img src={posterFor(p.cover) || p.cover} alt="" className="w-full h-full object-cover" />}
              </div>
            </Link>
          </li>
        ))}
      </ol>
    </div>
  );
};

const VideosLink = () => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  return (
    <Link to="/videos" data-testid="articles-videos-link" className="group flex items-center gap-4 rounded-2xl bg-slate-900 text-white p-5 hover:bg-slate-800 transition-colors">
      <span className="w-12 h-12 rounded-xl bg-amber-400 text-slate-900 flex items-center justify-center group-hover:scale-110 transition-transform"><PlayCircle className="w-6 h-6" /></span>
      <div>
        <div className="font-extrabold">{L('الفيديوهات التعليمية', 'Educational videos')}</div>
        <div className="text-slate-400 text-xs mt-0.5">{L('شاهد الشروحات العملية بالفيديو', 'Watch practical video tutorials')}</div>
      </div>
    </Link>
  );
};

export const ArticlesExtras = ({ mostRead }) => (
  <section data-testid="articles-extras" className="mt-14 grid lg:grid-cols-2 gap-6 items-start">
    <MostRead posts={mostRead} />
    <div className="space-y-6">
      <NewsletterBox />
      <VideosLink />
    </div>
  </section>
);
